import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Routes that should install as "GolfAuth Pro" instead of "GolfAuth".
// /pro is the Pro marketing landing; /pro/admin is the logged-in pro
// dashboard a pro lives in daily. Sign-in pages (/login, /register)
// and post-login intermediates (/select-golfer, /configurations) are
// transient — pros bookmark /admin directly — so they stay on the
// consumer manifest to avoid offering an install on a page nobody
// returns to.
const PRO_PATH_PREFIXES = ['/pro', '/pro/admin'];

function isProPath(pathname: string) {
  return PRO_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

// Swaps the active <link rel="manifest"> and the
// <meta name="apple-mobile-web-app-title"> based on the current route,
// so:
//   - Visitors on / (and consumer routes) install "GolfAuth" PWA
//   - Pros on /pro or /pro/admin install "GolfAuth Pro" PWA
//
// Browsers re-read the manifest when the link's href changes, so this
// swap is honoured at the next install-prompt firing (or the next
// Add-to-Home-Screen on iOS). The original href injected by
// vite-plugin-pwa is captured on first mount and restored when leaving
// pro routes — that way we don't hardcode the hashed-asset filename
// vite-plugin-pwa generates (it can change between builds).
export default function PWAManifestSwapper() {
  const { pathname } = useLocation();

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;

    // Capture the build-time default href once. dataset on the link
    // survives this component unmounting / remounting and survives
    // route changes — we just need somewhere DOM-attached to stash it.
    if (!link.dataset.defaultHref) {
      link.dataset.defaultHref = link.getAttribute('href') ?? '/manifest.webmanifest';
    }

    const isPro = isProPath(pathname);
    const desiredHref = isPro ? '/manifest-pro.webmanifest' : link.dataset.defaultHref;
    if (link.getAttribute('href') !== desiredHref) {
      link.setAttribute('href', desiredHref);
    }

    const appleTitle = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]',
    );
    const desiredAppleTitle = isPro ? 'GolfAuth Pro' : 'GolfAuth';
    if (appleTitle && appleTitle.content !== desiredAppleTitle) {
      appleTitle.content = desiredAppleTitle;
    }
  }, [pathname]);

  return null;
}
